import "./styles.css";
import { Vault } from "./store/vault";
import { emptyProfile, detectReprofilePrompts, type Profile } from "./profile/schema";
import { fetchPrivateTrends, fetchRates, fetchSeries, fetchSignals } from "./api";
import type { SeriesDetail } from "./signals/timing";
import { h, clear } from "./ui/dom";
import type { Actions, AppState, Tab } from "./ui/state";
import { renderCars, renderDashboard, renderProfile, renderProperty, renderWizard } from "./ui/views";
import { applyTheme } from "./ui/theme";
import { loadSignals, saveSignals } from "./store/marketCache";

const appEl = document.getElementById("app") as HTMLElement;
let state: AppState | null = null;

// Show the floating "back to top" button once the page has scrolled a bit.
// Wired once against the window (which survives app re-renders); it just probes
// for the current .to-top button, which is rebuilt on every render.
let scrollWired = false;
function wireScroll(): void {
  if (scrollWired) return;
  scrollWired = true;
  const onScroll = () => document.querySelector(".to-top")?.classList.toggle("show", window.scrollY > 480);
  window.addEventListener("scroll", onScroll, { passive: true });
}

// ---- lock / unlock screen -------------------------------------------------
function renderLock(mode?: "create"): void {
  Vault.migrateLegacy();
  const users = Vault.listUsers();
  const creating = mode === "create" || users.length === 0;
  const err = h("p", { class: "error" });
  const pin = h("input", { type: "password", placeholder: creating ? "Choose a PIN or passphrase (letters & numbers)" : "PIN or passphrase", autocomplete: "off" }) as HTMLInputElement;
  const nameInp = h("input", { type: "text", placeholder: "Profile name (e.g. Nick)", autocomplete: "off" }) as HTMLInputElement;
  const userSel = h("select", {}, ...users.map((u) => h("option", { value: u }, u))) as HTMLSelectElement;

  const submit = async () => {
    err.textContent = "";
    const vault = new Vault();
    try {
      let profile: Profile;
      if (creating) {
        const name = nameInp.value.trim();
        if (!name) throw new Error("Enter a profile name");
        if (pin.value.length < 4) throw new Error("PIN / passphrase needs at least 4 characters");
        profile = emptyProfile();
        await vault.initialize(name, pin.value, profile);
      } else {
        profile = await vault.unlock<Profile>(userSel.value, pin.value);
      }
      await boot(vault, profile);
    } catch (e) {
      err.textContent = (e as Error).message;
    }
  };
  pin.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") submit(); });

  clear(appEl);
  appEl.append(
    h("div", { class: "lock" },
      h("h1", {}, "SG Asset Feasibility"),
      h("p", { class: "lead-copy" }, "See what you can afford in Singapore property, and what a move really costs — SSD, CPF refund, stamp duties and all."),
      h("p", { class: "muted" },
        creating
          ? "Create your own profile — choose any name and your own PIN or passphrase. It's separate and private from every other profile on this device, encrypted with your PIN and never sent anywhere."
          : "Pick your profile and enter your PIN. Each profile has its own name + PIN and its own separately-encrypted data on this device."),
      creating ? nameInp : h("label", { class: "field" }, h("span", { class: "field-label" }, "Profile"), userSel),
      pin,
      err,
      h("button", { class: "primary", onclick: submit }, creating ? "Create profile" : "Unlock"),
      creating && users.length > 0
        ? h("button", { class: "link", onclick: () => renderLock() }, "← Back to existing profiles")
        : !creating
          ? h("button", { class: "link", onclick: () => renderLock("create") }, "+ Add another profile")
          : null,
      !creating
        ? h("button", { class: "link", onclick: () => { const u = userSel.value; if (confirm(`Delete profile "${u}" and its data? This cannot be undone.`)) { Vault.destroy(u); applyTheme();
renderLock(); } } }, "Delete selected profile")
        : null,
    ),
  );
}

// ---- boot after unlock ----------------------------------------------------
async function boot(vault: Vault, profile: Profile): Promise<void> {
  // Existing profiles (already have income) are treated as onboarded, so only
  // brand-new profiles see the guided wizard.
  if (profile.onboarded !== true && (profile.monthlyGrossIncome ?? 0) > 0) {
    profile.onboarded = true;
    try { await vault.save(profile); } catch { /* non-fatal */ }
  }
  state = {
    vault,
    profile,
    prevProfile: null,
    signals: [],
    signalsError: null,
    signalsLoading: true,
    signalsSavedAt: null,
    rates: null,
    trends: null,
    details: new Map(),
    // Always land on Home — it shows a real empty state with one next step
    // when the profile has no finances yet (UX_FINDINGS U-03).
    tab: "dashboard",
    reprofilePrompts: [],
  };
  render();
  await actions.refreshSignals();
  const rates = await fetchRates();
  if (state) { state.rates = rates; render(); }
  // Longitudinal trends load last (non-blocking) — powers the "trends are ready"
  // prompt and the district trend panel.
  const trends = await fetchPrivateTrends();
  if (state) { state.trends = trends; render(); }
}

// ---- actions --------------------------------------------------------------
const actions: Actions = {
  setTab(tab: Tab) { if (state) { state.tab = tab; render(); window.scrollTo({ top: 0 }); } },
  async saveProfile(next: Profile, prev?: Profile) {
    if (!state) return;
    const prompts = detectReprofilePrompts(prev ?? state.profile, next);
    next.updatedAt = new Date().toISOString();
    // Optimistic: update state + re-render immediately so simulations refresh
    // without waiting for the (async) encrypt + localStorage write.
    state.prevProfile = state.profile;
    state.profile = next;
    if (prompts.length) state.reprofilePrompts = prompts.map((q) => q.id);
    render();
    try { await state.vault.save(next); } catch (e) { console.error("vault save failed", e); }
  },
  async saveProfileQuiet(next: Profile) {
    if (!state) return;
    next.updatedAt = new Date().toISOString();
    state.profile = next; // update synchronously; persist in the background
    try { await state.vault.save(next); } catch (e) { console.error("vault save failed", e); }
    // deliberately no render() — caller updates the affected DOM in place
  },
  async openSeries(id: string): Promise<SeriesDetail | null> {
    if (!state) return null;
    if (state.details.has(id)) return state.details.get(id)!;
    try {
      const detail = await fetchSeries(id);
      state.details.set(id, detail);
      return detail;
    } catch { return null; }
  },
  async refreshSignals() {
    if (!state) return;
    try {
      state.signals = await fetchSignals();
      state.signalsError = null;
      state.signalsSavedAt = null;
      saveSignals(state.signals);
    } catch (e) {
      state.signalsError = (e as Error).message;
      // Fall back to the last good copy rather than an empty screen.
      const cached = loadSignals();
      if (cached && state.signals.length === 0) {
        state.signals = cached.series;
        state.signalsSavedAt = cached.savedAt;
      }
    }
    state.signalsLoading = false;
    render();
  },
  lock() { if (state) { state.vault.lock(); state = null; renderLock(); } },
  dismissReprofile() { if (state) { state.reprofilePrompts = []; render(); } },
};

// ---- shell + routing ------------------------------------------------------
function render(): void {
  if (!state) return renderLock();

  // Brand-new profiles get the guided setup wizard (its own full-screen flow)
  // until they finish or skip it.
  if (state.profile.onboarded !== true) {
    clear(appEl);
    appEl.append(
      h("header", { class: "topbar" },
        h("div", { class: "brand" }, "SG Asset Feasibility"),
        h("button", { class: "tab", title: "Lock / switch profile", onclick: () => actions.lock() }, `🔒 ${state.vault.user ?? ""}`),
      ),
      h("main", { class: "container" }, renderWizard(state, actions)),
    );
    return;
  }

  // Home first, settings last (UX_FINDINGS U-17); asset tabs shown per interest.
  const int = state.profile.interests ?? { hdb: true, private: true, car: true };
  const tabs = ([
    ["dashboard", "Home"],
    ["property", "Property"],
    ["cars", "Cars"],
    ["profile", "Profile"],
  ] as [Tab, string][]).filter(([t]) =>
    t === "property" ? int.hdb || int.private : t === "cars" ? int.car : true,
  );
  // If the active tab was just hidden, fall back to a visible one.
  if (!tabs.some(([t]) => t === state!.tab)) state.tab = "profile";

  // Emoji glyphs for the mobile bottom bar (icon + label, thumb-reachable).
  const tabIcon: Record<Tab, string> = { profile: "👤", dashboard: "⌂", property: "🏠", cars: "🚗" };
  const topTab = (t: Tab, label: string) =>
    h("button", { class: state!.tab === t ? "tab active" : "tab", "aria-current": state!.tab === t ? "page" : null, onclick: () => actions.setTab(t) }, label);
  const bottomTab = (t: Tab, label: string) =>
    h("button", { class: state!.tab === t ? "bnav-btn active" : "bnav-btn", "aria-current": state!.tab === t ? "page" : null, onclick: () => actions.setTab(t) },
      h("span", { class: "bnav-ico", "aria-hidden": "true" }, tabIcon[t] ?? "•"),
      h("span", { class: "bnav-label" }, label));

  clear(appEl);
  appEl.append(
    h("header", { class: "topbar" },
      h("div", { class: "brand" }, "SG Asset Feasibility"),
      // Top nav — hidden on phones (the bottom bar takes over there).
      h("nav", { class: "tabs tabs-top", "aria-label": "Primary" }, ...tabs.map(([t, label]) => topTab(t, label))),
      h("button", { class: "tab lock-btn", title: "Lock / switch profile", onclick: () => actions.lock() },
        `🔒 ${state.vault.user ?? ""}`),
    ),
  );
  let body: HTMLElement;
  try {
    body =
      state.tab === "dashboard" ? renderDashboard(state, actions)
      : state.tab === "property" ? renderProperty(state, actions)
      : state.tab === "cars" ? renderCars(state, actions)
      : renderProfile(state, actions);
  } catch (e) {
    body = h("section", { class: "view" },
      h("p", { class: "error" }, `Something went wrong rendering this tab: ${(e as Error).message}`),
      h("button", { class: "ghost", onclick: () => actions.setTab("dashboard") }, "Back to dashboard"));
  }
  appEl.append(h("main", { class: "container" }, body));

  // Floating back-to-top — appears once the page is scrolled (see wireScroll).
  appEl.append(
    h("button", { class: "to-top", title: "Back to top", "aria-label": "Back to top",
      onclick: () => window.scrollTo({ top: 0, behavior: "smooth" }) }, "↑"),
  );

  // Mobile bottom navigation — fixed, thumb-reachable; CSS reveals it only on
  // phone widths (the top nav is hidden there).
  appEl.append(
    h("nav", { class: "bottom-nav", "aria-label": "Primary" },
      ...tabs.map(([t, label]) => bottomTab(t, label))),
  );
  wireScroll();
  document.querySelector(".to-top")?.classList.toggle("show", window.scrollY > 480);
}

renderLock();
